import {
  Box,
  Grid,
  Hidden,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import isValidEmail, {
  getPlatform,
  pushToDataLayer,
  setAuthToken,
} from "../constants/utils";
import PrimaryButton from "../customComponents/CustomPrimaryButton";
import useAxiosAuth from "../hooks/useAxiosAuth";
import "../styles/css/login.css";
import CustomField from "./../customComponents/CustomField";
import { onChangeValue as onChangeHome } from "./../home/homeSlice";
import { ReactComponent as EmailIcon } from "./../images/icons/emailIcon.svg";
import { ReactComponent as EyeIcon } from "./../images/icons/eyeIcon.svg";
import { ReactComponent as EyeIconCrossed } from "./../images/icons/eyeIconCrossed.svg";
import { ReactComponent as LockIcon } from "./../images/icons/lockIcon.svg";
import loginImage from "./../images/loginImage.png";
import { ReactComponent as TelDripLogosvg } from "./../images/logos/telDripLogo.svg";
import ForgotPasswordDialog from "./ForgotPasswordDialog";
import { onChangeValue, resetStore } from "./loginSlice";
import Loader from "../customComponents/Loader";

function Login(props) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const axiosAuth = useAxiosAuth();
  const [loading, setLoading] = useState(false);
  const userData = JSON.parse(localStorage.getItem("user_data"));
  const planData = JSON.parse(sessionStorage.getItem("planData"));
  const isSwitch = useSelector((state) => state.profileData.isSwitch);

  // Redirect if user data exists in localStorage
  useEffect(() => {
    if (userData?.plan_type) {
      navigate("/dashboard"); // Redirect to the dashboard if userData is available
    }
  }, [userData, navigate]);

  useEffect(() => {
    function hasJWT() {
      let flag = false;
      localStorage.getItem("access_token") ? (flag = true) : (flag = false);
      return flag;
    }

    if (hasJWT() && userData?.plan_type !== null) {
      navigate("/dashboard");
    }

    return () => {
      dispatch(resetStore());
    };
  }, []);

  const email = useSelector((state) => state.login.email);
  const password = useSelector((state) => state.login.password);
  const visibility = useSelector((state) => state.login.visibility);
  const passwordDialog = useSelector((state) => state.login.passwordDialog);
  const errors = useSelector((state) => state.login.errors);
  const userSystemInfo = useSelector((state) => state.home.userSystemInfo);

  const onEmailChange = (e) => {
    if (!!e.target.value && !!errors["email"]) {
      let errObj = { ...errors };
      delete errObj["email"];
      dispatch(onChangeValue({ name: "errors", value: errObj }));
    }

    dispatch(onChangeValue({ name: "email", value: e.target.value.trim() }));
  };

  const onPasswordChange = (e) => {
    if (!!e.target.value && !!errors["email"]) {
      let errObj = { ...errors };
      delete errObj["password"];
      dispatch(onChangeValue({ name: "errors", value: errObj }));
    }

    dispatch(onChangeValue({ name: "password", value: e.target.value }));
  };

  const onClickShowPassword = () => {
    dispatch(onChangeValue({ name: "visibility", value: !visibility }));
  };

  const onForgotPasswordClick = (e) => {
    e.stopPropagation();
    dispatch(onChangeValue({ name: "passwordDialog", value: true }));
  };

  const closePasswordDialog = () => {
    dispatch(onChangeValue({ name: "passwordDialog", value: false }));
  };

  const getDialogs = () => {
    return (
      <>
        <ForgotPasswordDialog
          open={passwordDialog}
          onClose={closePasswordDialog}
        />
      </>
    );
  };

  const getUserTimeZone = async (id, authToken) => {
    try {
      let response = await axiosAuth.get(`/api/updateTimeZone/?user=${id}`, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      if (response?.status === 200) {
        return response.data;
      }
    } catch (error) {}
  };

  const loginHistoryUpdateHandler = async (body, authToken) => {
    try {
      let response = await axiosAuth.post(`/api/loginHistory/`, body, {
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
    } catch (error) {}
  };

  const onClickLogin = async (e) => {
    e?.preventDefault();
    let errorsObj = { ...errors };

    if (!email) {
      errorsObj = { ...errorsObj, email: "Please enter your email id" };
    } else {
      if (!isValidEmail(email)) {
        errorsObj = { ...errorsObj, email: "Please enter valid email id" };
      }
    }

    if (!password) {
      errorsObj = { ...errorsObj, password: "Please enter password" };
    }

    dispatch(onChangeValue({ name: "errors", value: { ...errorsObj } }));

    if (Object.keys(errorsObj)?.length === 0) {
      setLoading(true);
      try {
        const res = await axiosAuth.post("/api/login", { email, password });

        if (res.status === 200 || res.status === 201) {
          const token = res.data.access_token;
          const userTimeZone = await getUserTimeZone(res?.data?.user_id, token);

          let loginHistoryStamp = {
            userCompanies: res.data.user_id,
            timestamp: moment()
              .tz(userTimeZone)
              .format("YYYY-MM-DDTHH:mm:ss.SS"),
            ip: userSystemInfo,
            os: getPlatform(),
            logout: null,
            loginStatus: true,
          };

          await loginHistoryUpdateHandler(loginHistoryStamp, token);

          let tempUserData = { ...res.data };
          if (res.data.user_type === "admin") {
            localStorage.setItem("access_token", token);
            localStorage.setItem("status", res?.data?.status);
          }
          setAuthToken(token);

          dispatch(
            onChangeHome({ name: "userData", value: { ...tempUserData } })
          );

          if (res?.data?.user_type !== "admin") redirectToEnvironment(res);
          else {
            localStorage.setItem("user_data", JSON.stringify(tempUserData));
            if (planData?.selectedPlan && !res?.data?.is_payment) {
              navigate("/payment-details", {
                state: {
                  selectedPlan: planData?.selectedPlan,
                },
              });
              return;
            }
            if (res?.data?.plan_type === "Custom" && !res?.data?.is_payment) {
              navigate("/payment-details");
              return;
            } else {
              if (tempUserData.plan_type === null) {
                if (planData?.selectedPlan) {
                  navigate("/payment-details", {
                    state: {
                      selectedPlan: planData?.selectedPlan,
                    },
                  });
                  return;
                } else {
                  navigate("/pricing");
                  return;
                }
              }
            }
            toast.success(`Login Successful`);
            navigate("/dashboard");
            dispatch(onChangeHome({ name: "selectedKey", value: "0" }));
          }
          pushToDataLayer("login_successful");
        } else {
          toast.error("There is some error in login");
          pushToDataLayer("login_failed");
        }
      } catch (error) {
        setLoading(false);
        if (
          error?.response?.status === 401 ||
          error?.response?.status === 400
        ) {
          toast.error(
            error.response.data.detail ??
              error.response.data.message ??
              "Something went wrong"
          );
        }
        pushToDataLayer("login_failed");
      }
    }
  };

  const redirectToEnvironment = async (res) => {
    const { user_type, user_id, user_email, access_token } = res?.data || {};
    const params = `id=${user_id}&user_type=${user_type}&email=${user_email}&token=${access_token}&type=login`;

    if (window.location.href.includes("localhost")) {
      if (user_type === "publisher") {
        window.location.href = `http://localhost:3001/login-request?${params}`;
      } else if (user_type === "advertiser") {
        window.location.href = `http://localhost:3002/login-request?${params}`;
      }
    } else if (
      window.location.href.includes("dev") ||
      window.location.href.includes("qa")
    ) {
      let prefix = window.location.href.includes("qa") ? "qa" : "dev";
      window.location.href = `https://${prefix}${user_type}.teldrip.com/login-request?${params}`;
    } else if (
      !window.location.href.includes("dev") &&
      !window.location.href.includes("qa") &&
      window.location.href.includes("teldrip")
    ) {
      window.location.href = `https://${user_type}.teldrip.com/login-request?${params}`;
    }
  };

  const getErrorMessage = (msg) => {
    return (
      <Typography
        style={{ color: "#FF7070", fontSize: "16px", paddingRight: "4px" }}
      >
        {msg}
      </Typography>
    );
  };

  // If user data is available, show a loader instead of the login form
  if (userData?.plan_type) {
    return <Loader />;
  }

  if (isSwitch) return <Loader />;
  else
    return (
      <>
        <Grid
          container
          alignItems="stretch"
          minHeight="100vh"
          margin="0"
          padding="0"
          className="login-screen"
        >
          <Grid
            item
            xs={12}
            md={6}
            height="auto"
            backgroundColor="var(--login-left-screen-bg)"
            position="relative"
            display="flex"
            alignItems="center"
            justifyContent="center"
            className="login-screen-left"
          >
            <Box
              sx={{
                position: "absolute",
                top: "30px",
                left: "30px",
                zIndex: 3,
              }}
            >
              <TelDripLogosvg width="150" />
            </Box>
            <Box maxWidth="600px" p="5rem 0">
              <img
                src={loginImage}
                width="800"
                height="800"
                className="img-fluid"
                alt="loginGif"
                style={{ maxWidth: "600px" }}
              />
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            height="auto"
            width={1}
            alignItems="center"
            position="relative"
            display="flex"
            className="login-screen-right"
          >
            <Hidden mdUp>
              <Box
                sx={{
                  position: "absolute",
                  top: "30px",
                  left: "30px",
                }}
              >
                <TelDripLogosvg width="150" />
              </Box>
            </Hidden>
            <Box maxWidth="500px" width={1} margin="auto">
              <Typography
                variant="h3"
                component="h1"
                color="var(--text-body-title)"
                paddingBottom="5rem"
              >
                Login
              </Typography>
              <Box pb={3}>
                <CustomField
                  fullWidth
                  value={email}
                  onChange={onEmailChange}
                  placeholder="Enter Your Email ID"
                  heading="Email ID"
                  error={Boolean(errors["email"])}
                  errorMessage={getErrorMessage(errors["email"])}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="var(--custom-field-input-color)" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box>
                <CustomField
                  fullWidth
                  type={visibility ? "text" : "password"}
                  // value={password}
                  value={password.replace(" ", "")}
                  onChange={onPasswordChange}
                  heading="Password"
                  placeholder="Enter Password"
                  error={Boolean(errors["password"])}
                  errorMessage={getErrorMessage(errors["password"])}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon color="var(--custom-field-input-color)" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={onClickShowPassword}>
                          {!!visibility ? (
                            <EyeIcon color="var(--custom-field-input-color)" />
                          ) : (
                            <EyeIconCrossed color="var(--custom-field-input-color)" />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      onClickLogin(event);
                    }
                  }}
                />
              </Box>
              <Box pt={1} pb={6} display="flex" justifyContent="flex-end">
                <Typography
                  component={Link}
                  onClick={onForgotPasswordClick}
                  sx={{
                    color: "var(--login-highlight-text)",
                    background: "transparent",
                  }}
                >
                  Forgot Password?
                </Typography>
              </Box>
              <Box pb={3}>
                <PrimaryButton
                  fullWidth
                  onClick={onClickLogin}
                  isRejectDialog={loading}
                >
                  Login
                </PrimaryButton>
              </Box>
              <Typography
                color="var(--text-body-title)"
                style={{ fontWeight: 500, textAlign: "center" }}
              >
                Don’t have an account?
                <Typography
                  component={Link}
                  to="/signup"
                  sx={{
                    color: "var(--login-highlight-text)",
                    paddingLeft: "2px",
                    textDecoration: "none",
                  }}
                >
                  Sign Up
                </Typography>
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <Box component="section" display="flex">
          {getDialogs()}
        </Box>
      </>
    );
}

export default Login;
